import type { ReactNode } from 'react'

export type HistoryEntityKind = 'user' | 'organization' | 'product' | 'certification'

export type HistoryEntityLink = {
  kind: HistoryEntityKind
  id: string
  label: string
}

type LinkMatch = {
  start: number
  end: number
  link: HistoryEntityLink
}

/**
 * Resolve entity links for a history row.
 * Prefers explicit meta.links; falls back to users[] + organizationId/Name.
 */
export function resolveHistoryEntityLinks(input: {
  users?: Array<{ id?: string; displayName?: string }>
  meta?: Record<string, unknown>
  organizationId?: string | null
}): HistoryEntityLink[] {
  const fromMeta = parseLinks(input.meta?.links)
  if (fromMeta.length > 0) return fromMeta

  const links: HistoryEntityLink[] = []
  const seen = new Set<string>()

  for (const user of input.users ?? []) {
    const id = user.id?.trim()
    const label = user.displayName?.trim()
    if (!id || !label) continue
    const key = `user:${id}:${label}`
    if (seen.has(key)) continue
    seen.add(key)
    links.push({ kind: 'user', id, label })
  }

  const orgId =
    (typeof input.meta?.organizationId === 'string'
      ? input.meta.organizationId
      : null) ??
    input.organizationId ??
    null
  const orgName =
    typeof input.meta?.organizationName === 'string'
      ? input.meta.organizationName
      : null
  if (orgId && orgName?.trim()) {
    const label = orgName.trim()
    const key = `organization:${orgId}:${label}`
    if (!seen.has(key)) {
      seen.add(key)
      links.push({ kind: 'organization', id: orgId, label })
    }
  }

  return links
}

function parseLinks(raw: unknown): HistoryEntityLink[] {
  if (!Array.isArray(raw)) return []
  const out: HistoryEntityLink[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const row = entry as Record<string, unknown>
    const kind = String(row.kind ?? '')
    const id = String(row.id ?? '').trim()
    const label = String(row.label ?? '').trim()
    if (
      (kind === 'user' ||
        kind === 'organization' ||
        kind === 'product' ||
        kind === 'certification') &&
      id &&
      label
    ) {
      out.push({ kind, id, label })
    }
  }
  return out
}

/**
 * Split a summary into text/link segments by matching link labels
 * (longest-first, non-overlapping).
 */
export function linkifyHistorySummary(
  summary: string,
  links: HistoryEntityLink[],
  renderLink: (link: HistoryEntityLink, key: string) => ReactNode,
): ReactNode[] {
  if (!summary) return []
  if (!links.length) return [summary]

  const sorted = [...links].sort((a, b) => b.label.length - a.label.length)
  const matches: LinkMatch[] = []
  const claimed = new Array<boolean>(summary.length).fill(false)

  for (const link of sorted) {
    if (!link.label) continue
    let from = 0
    while (from < summary.length) {
      const idx = summary.indexOf(link.label, from)
      if (idx < 0) break
      const end = idx + link.label.length
      let overlaps = false
      for (let i = idx; i < end; i++) {
        if (claimed[i]) {
          overlaps = true
          break
        }
      }
      if (!overlaps) {
        matches.push({ start: idx, end, link })
        for (let i = idx; i < end; i++) claimed[i] = true
      }
      from = end
    }
  }

  matches.sort((a, b) => a.start - b.start)

  const nodes: ReactNode[] = []
  let cursor = 0
  matches.forEach((match, i) => {
    if (match.start > cursor) {
      nodes.push(summary.slice(cursor, match.start))
    }
    nodes.push(renderLink(match.link, `${match.link.kind}-${match.link.id}-${i}`))
    cursor = match.end
  })
  if (cursor < summary.length) {
    nodes.push(summary.slice(cursor))
  }
  return nodes
}
